import React from 'react';
import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';

import styles from './styles';
import { SORT_DIRECTIONS } from '../../constants';
import { sortObjectByKey } from '../../utilities/helpers';

const propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number,
      title: PropTypes.string,
      sort: PropTypes.shape({
        enabled: PropTypes.bool,
        customSort: PropTypes.func,
      }).isRequired,
      name: PropTypes.string,
    }),
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  classes: PropTypes.shape({
    firstCol: PropTypes.string,
    headerCell: PropTypes.string,
    sortArrow: PropTypes.string,
    clickable: PropTypes.string,
    sortArrowEnabled: PropTypes.string,
    sortArrowDown: PropTypes.string,
    tableRow: PropTypes.string,
    cell: PropTypes.string,
    customCell: PropTypes.string,
    noData: PropTypes.string,
    tableWrapper: PropTypes.string,
    table: PropTypes.string,
  }).isRequired,
  onSort: PropTypes.func,
  sort: PropTypes.shape({
    column: PropTypes.string,
    direction: PropTypes.oneOf([SORT_DIRECTIONS.DESC, SORT_DIRECTIONS.ASC]),
  }),
  noDataMessage: PropTypes.string,
  defaultSortDirection: PropTypes.oneOf([
    SORT_DIRECTIONS.DESC,
    SORT_DIRECTIONS.ASC,
  ]),
  // indicate if the first column is sticky
  sticky: PropTypes.bool,
};

const defaultProps = {
  noDataMessage: 'No jobs yet, 😢 Create a new job ↗️',
  defaultSortDirection: SORT_DIRECTIONS.DESC,
  onSort: null,
  sort: null,
  sticky: true,
};

export class DataTable extends React.Component {
  handleSort = newColumn => {
    const { sort, onSort, defaultSortDirection } = this.props;

    // double security here to make sure if sort prop is null or onSort prop is null
    // we do not do sorting
    if (!sort || !onSort) return;

    // sort direction is 'descending' by default
    let newDirection = defaultSortDirection;

    // we are sorting the same column, we change the sort direction to the other value
    if (newColumn.name === sort.column) {
      // calculate the new direction
      newDirection =
        sort.direction === SORT_DIRECTIONS.DESC
          ? SORT_DIRECTIONS.ASC
          : SORT_DIRECTIONS.DESC;
    }

    onSort(newColumn.name, newDirection);
  };

  renderTableHeader = () => {
    const { classes, columns, sort, sticky } = this.props;

    return (
      <thead>
        <tr>
          {columns
            .sort(sortObjectByKey('index', SORT_DIRECTIONS.ASC))
            .map((col, index) => {
              let cellClassName =
                index === 0 && sticky
                  ? classNames(
                      classes.firstCol,
                      classes.headerCell,
                      kebabCase(col.name),
                    )
                  : classNames(classes.headerCell, kebabCase(col.name));

              // this column is sortable and sort prop is not null
              if (col.sort.enabled && sort) {
                let sortArrowClassName = classes.sortArrow;
                cellClassName = classNames(cellClassName, classes.clickable);

                // this column is currently being sorted
                if (col.name === sort.column) {
                  sortArrowClassName = classNames(
                    sortArrowClassName,
                    classes.sortArrowEnabled,
                  );

                  // this column's sorting order is descending
                  if (sort.direction === SORT_DIRECTIONS.DESC) {
                    sortArrowClassName = classNames(
                      sortArrowClassName,
                      classes.sortArrowDown,
                    );
                  }
                }

                return (
                  <th
                    key={col.name}
                    className={cellClassName}
                    onClick={() => this.handleSort(col)}
                    data-test="sortable-column-header-cell"
                  >
                    <span>
                      {col.title}
                      <ArrowDropUpIcon className={sortArrowClassName} />
                    </span>
                  </th>
                );
              }

              return (
                <th
                  key={col.name}
                  className={cellClassName}
                  data-test="unsortable-column-header-cell"
                >
                  {col.title}
                </th>
              );
            })}
        </tr>
      </thead>
    );
  };

  renderRow = (row, index) => {
    const { classes } = this.props;

    return (
      <tr key={index} className={classes.tableRow} data-test="data-table-row">
        {this.renderRowCells(row)}
      </tr>
    );
  };

  renderRowCells = row => {
    const { classes, columns, sticky } = this.props;

    return columns
      .sort(sortObjectByKey('index', SORT_DIRECTIONS.ASC))
      .map((col, index) => {
        const normalCellClassName =
          index === 0 && sticky
            ? classNames(classes.firstCol, classes.cell, kebabCase(col.name))
            : classNames(classes.cell, kebabCase(col.name));

        const customCellClassName =
          index === 0 && sticky
            ? classNames(
                classes.firstCol,
                classes.customCell,
                kebabCase(col.name),
              )
            : classNames(classes.customCell, kebabCase(col.name));

        if (col.cell) {
          if (row.rowLeftBarColor && index === 0) {
            return (
              <td
                className={customCellClassName}
                key={col.name}
                style={{ backgroundColor: row.rowLeftBarColor }}
              >
                <div>{col.cell(row)}</div>
              </td>
            );
          }
          return (
            <td className={customCellClassName} key={col.name}>
              <div>{col.cell(row)}</div>
            </td>
          );
        }

        if (row.rowLeftBarColor && index === 0) {
          return (
            <td
              className={normalCellClassName}
              key={col.name}
              style={{ backgroundColor: row.rowLeftBarColor }}
            >
              <div>{row[col.name]}</div>
            </td>
          );
        }

        return (
          <td className={normalCellClassName} key={col.name}>
            <div>{row[col.name]}</div>
          </td>
        );
      });
  };

  renderContent = () => {
    const { data, columns, classes, noDataMessage } = this.props;

    if (data.length === 0) {
      return (
        <tbody>
          <tr>
            <td className={classes.noData} colSpan={columns.length}>
              {noDataMessage}
            </td>
          </tr>
        </tbody>
      );
    }

    return (
      <tbody>{data.map((row, index) => this.renderRow(row, index))}</tbody>
    );
  };

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.tableWrapper}>
        <table className={classes.table}>
          {this.renderTableHeader()}
          {this.renderContent()}
        </table>
      </div>
    );
  }
}

DataTable.propTypes = propTypes;
DataTable.defaultProps = defaultProps;

export default withStyles(styles)(DataTable);
